/**
 * What the printed proposal says, worked out once from what exists.
 *
 * The document (content.sections), the price list (catalog_services) and the
 * studio's settings (owner, firm details) are read into one flat model, and
 * the three looks draw that model. Nothing here invents a price: every figure
 * comes from the investment section's rows or its frozen totals. A page with
 * no data behind it is left out by the renderer, not drawn empty.
 *
 * Kept free of imports from documentSections so the two can import each
 * other's exports without a cycle at load time.
 */

import { STANDARD_COPY } from '../proposalLooks'
import { scheduleServices, stageWeeks, milestonesFrom } from '../proposalSchedule'
import { proposalVars, resolveDeep } from '../proposalVars'
import { APPROACH } from './copy'

export const text = (v) => String(v ?? '').trim()

export const money = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(n) || 0)

const realRows = (rows) =>
  (Array.isArray(rows) ? rows : []).filter(r => text(r?.label) || Number(r?.amount) > 0)

const looksLikeHtml = (s) => /<\/?[a-z][^>]*>/i.test(String(s || ''))

/**
 * Plain text from a field that may hold HTML. Older proposals kept a whole
 * designed page in the overview body; printed raw it would read as markup.
 */
export function plain(s) {
  const v = String(s ?? '')
  if (!looksLikeHtml(v)) return v.trim()
  return v
    .replace(/<(br|\/p|\/div|\/h\d|\/li|\/tr)\b[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&#39;|&rsquo;/g, '’').replace(/&quot;/g, '"')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n\s*\n\s*(\n\s*)+/g, '\n\n')
    .split('\n').map(l => l.trim()).join('\n')
    .trim()
}

export const isHtml = looksLikeHtml

/** "2–3 weeks", "Weeks 1–3", "4 weeks", 6 → the longest it can take, in weeks. */
export function weeksFrom(schedule, service) {
  const s = text(schedule)
  if (/month/i.test(s)) return null
  const range = s.match(/(\d+)\s*[–-]\s*(\d+)/)
  if (range) return Number(range[2]) || null
  const one = s.match(/(\d+)\s*(week|wk)/i) || s.match(/^(\d+)$/)
  if (one) return Number(one[1]) || null
  const hi = Number(service?.weeks_max) || Number(service?.weeks_min)
  return hi || null
}

/**
 * Deliverables from a sentence of scope. "Naming territories, 20–30
 * candidates narrowed to three, preliminary screening for domain and obvious
 * conflicts, plus a tagline" becomes four short lines.
 */
export function deliverablesFrom(scope, max = 8) {
  const s = plain(scope).replace(/\s+/g, ' ')
  if (!s) return []
  const parts = []
  // Split on sentence ends, semicolons, list dashes and commas that are not
  // inside brackets; then on a trailing ", plus" / " plus ".
  let depth = 0
  let cur = ''
  for (let i = 0; i < s.length; i += 1) {
    const c = s[i]
    if (c === '(') depth += 1
    if (c === ')') depth = Math.max(0, depth - 1)
    const end = depth === 0 && (c === ';' || c === ',' || ((c === '.' || c === ':') && s[i + 1] === ' ') || (c === '—' && s[i + 1] === ' '))
    if (end) { parts.push(cur); cur = '' } else cur += c
  }
  parts.push(cur)
  const out = []
  for (let p of parts) {
    p = p.replace(/^\s*(plus|and|including)\s+/i, '').replace(/[.\s]+$/, '').trim()
    if (!p) continue
    // A long line with an "and" in it reads better as two.
    if (p.length > 48 && / and /.test(p)) {
      const [a, ...rest] = p.split(/ and /)
      out.push(a, rest.join(' and '))
    } else {
      out.push(p)
    }
  }
  return out
    .map(p => p.trim().replace(/^\w/, c => c.toUpperCase()))
    .filter(p => p.length > 2)
    .slice(0, max)
}

const HONORIFIC = /^(dr|mr|mrs|ms|mx|prof|rev)\.?$/i
/** "Ana" from "Ana Alvarez"; "Dr. Alvarez" from "Dr. Ana Alvarez". */
export const firstName = (name) => {
  const w = text(name).split(/\s+/).filter(Boolean)
  if (!w.length) return ''
  if (HONORIFIC.test(w[0])) return w.length > 1 ? `${w[0]} ${w[w.length - 1]}` : w[0]
  return w[0]
}

/** The section's index and data, by type. */
const find = (doc, type) => {
  const i = (doc.sections || []).findIndex(s => s.type === type)
  return i < 0 ? { index: -1, data: null } : { index: i, data: doc.sections[i].data || {} }
}

/**
 * The whole model.
 *
 * `settings` is { owner_name, owner_email, avatar_url, signature_url, about,
 * firm: { phone, address, website, signer_name, signer_title } }; every key is
 * optional. `catalog` is the studio's loadCatalog() result, or { services }.
 */
export function buildModel(rawDoc = {}, { settings = {}, catalog = {}, resolve = false } = {}) {
  const firm = settings.firm || {}
  const services = Array.isArray(catalog.services) ? catalog.services : []
  const byId = Object.fromEntries(services.map(s => [s.id, s]))
  // {{variables}} in the text are filled for print; the editable canvas keeps
  // them as written so editing a letter never bakes a value in.
  const vars = modelVars(rawDoc, { settings, byId })
  const doc = resolve ? { ...rawDoc, ...resolveDeep({ title: rawDoc.title, sections: rawDoc.sections || [], team: rawDoc.team || [] }, vars) } : rawDoc

  const recipient = text(doc.recipient)
  const contact = text(doc.contact)

  const owner = {
    name: text(settings.owner_name) || text(firm.signer_name) || 'Efren Cavazos',
    title: text(firm.signer_title) || 'Founder and creative director',
    email: text(settings.owner_email),
    phone: text(firm.phone),
    website: text(firm.website).replace(/^https?:\/\//, '').replace(/\/$/, '') || 'abstract-creative.com',
    address: text(firm.address),
    avatar: text(settings.avatar_url),
    signature: text(settings.signature_url),
  }

  /* ── Scope: one block per service ──────────────────────────────────── */
  const scope = find(doc, 'scope')
  const items = scope.data ? (scope.data.items || []) : []
  // When each service runs, with the studio's overlaps (proposalSchedule).
  const plan = scheduleServices(items, byId)
  const barAt = Object.fromEntries(plan.bars.map(b => [b.index, b]))
  const blocks = []
  items.forEach((it, i) => {
    if (!(text(it?.title) || text(it?.description))) return
    const svc = it.service_id ? byId[it.service_id] : null
    const own = Array.isArray(it.deliverables) && it.deliverables.filter(d => text(d)).length > 0
    const derived = own ? [] : deliverablesFrom(svc?.internal_scope || '')
    const outcome = text(svc?.client_facing) || text(svc?.summary)
    const retainer = it.kind === 'retainer' || /month/i.test(text(it.schedule))
    const bar = retainer ? null : barAt[i]
    const weeks = bar ? bar.weeks : null
    const start = bar ? bar.start : null
    const end = bar ? bar.end : null
    blocks.push({
      index: i,
      title: text(it.title),
      description: text(it.description),
      outcome: outcome || text(it.description),
      outcomeIsDescription: !outcome,
      deliverables: own ? it.deliverables : derived,
      ownDeliverables: own,
      notIncluded: text(svc?.not_included),
      schedule: text(it.schedule),
      retainer,
      weeks,
      start,
      end,
      amount: Number(it.amount) || 0,
    })
  })
  const totalWeeks = plan.totalWeeks || null

  // The five stages: the words the proposal carries (generated, editable),
  // the weeks worked out from the services every time it is drawn.
  const stageRange = stageWeeks(plan.bars, plan.totalWeeks)
  const ownStages = Array.isArray(scope.data?.approach) ? scope.data.approach.filter(st => text(st?.name)) : []
  const stages = (ownStages.length ? ownStages : APPROACH.map(([name, what, time]) => ({ name, what, time })))
    .slice(0, 5)
    .map((st, i) => ({ name: text(st.name), what: text(st.what), time: text(st.time), range: stageRange[i] || null, path: ownStages.length ? `sections.${scope.index}.data.approach.${i}` : null }))
  const milestones = milestonesFrom(plan.bars)

  /* ── Investment ────────────────────────────────────────────────────── */
  const inv = find(doc, 'investment')
  const invData = inv.data || {}
  const rows = realRows(invData.rows)
  const totals = invData.totals || null
  const total = totals
    ? (Number(totals.oneTimeTotal) || 0)
    : rows.filter(r => r.kind !== 'retainer').reduce((n, r) => n + (Number(r.amount) || 0), 0)
  const monthly = totals ? Number(totals.monthly) || 0 : rows.filter(r => r.kind === 'retainer').reduce((n, r) => n + (Number(r.amount) || 0), 0)
  const schedule = totals && Array.isArray(totals.schedule) ? totals.schedule.filter(p => Number(p.amount) > 0) : []

  // Add-ons the client can choose, as the proposal carries them. Never priced
  // here: a row with no amount is listed without one.
  const addons = (Array.isArray(doc.addons) ? doc.addons : [])
    .map(a => ({ label: text(a.label || a.name || a.title), amount: Number(a.amount || a.price) || 0, unit: a.kind === 'retainer' || a.monthly ? '/mo' : '' }))
    .filter(a => a.label)

  // Alternatives: content.options as the old builder wrote them.
  const options = (Array.isArray(doc.options) ? doc.options : [])
    .map(o => ({
      name: text(o.name || o.label || (o.option ? `Option ${o.option}` : '')),
      structure: text(o.structure || o.description || o.detail),
      investment: text(typeof o.investment === 'number' ? money(o.investment) : o.investment || (Number(o.amount) > 0 ? money(o.amount) : '')),
      weeks: text(o.weeks),
      items: Array.isArray(o.items) ? o.items.map(text).filter(Boolean) : [],
      recommended: Boolean(o.recommended),
    }))
    .filter(o => o.name && (o.structure || o.investment))

  /* ── Proof, library pages ──────────────────────────────────────────── */
  const proof = find(doc, 'proof')
  const proofData = proof.data || {}
  const stats = (proofData.stats || []).map((s, i) => ({ ...s, _i: i })).filter(s => text(s?.figure))
  const quotes = (proofData.quotes || []).map((q, i) => ({ ...q, _i: i })).filter(q => text(q?.quote))

  const pages = (doc.sections || [])
    .map((s, index) => ({ index, ...(s.data || {}), type: s.type }))
    .filter(s => s.type === 'page' && (text(s.title) || text(s.body)))
  const cases = pages.filter(p => p.kind === 'case_study')
  const otherPages = pages.filter(p => p.kind !== 'case_study' && p.kind !== 'about')
  const aboutPage = pages.find(p => p.kind === 'about')
  const about = find(doc, 'about')

  /* ── Terms ─────────────────────────────────────────────────────────── */
  const termsSec = find(doc, 'terms')
  const ownTerms = (termsSec.data?.items || []).filter(t => text(t?.title) || text(t?.body))
  const paymentLine = schedule.length
    ? `${schedule.map(p => `${p.pct}% ${text(p.label).toLowerCase()}`).join(', ')}. Due within 15 days, by card or bank transfer.`
    : null
  const terms = ownTerms.length
    ? ownTerms.map((t, i) => ({ title: text(t.title), body: text(t.body), path: `sections.${termsSec.index}.data.items.${i}` }))
    : STANDARD_COPY.terms.map(([title, body]) => ({
      title,
      body: title === 'Payment' && paymentLine ? paymentLine : body,
      path: null,
    }))

  const overview = find(doc, 'overview')
  const acceptance = find(doc, 'acceptance')
  const coverSec = find(doc, 'cover')

  const pairs = (list) => (Array.isArray(list) ? list : [])
    .map(x => (Array.isArray(x) ? [text(x[0]), text(x[1])] : [text(x?.title ?? x?.q), text(x?.body ?? x?.a)]))
    .filter(([k]) => k)
  const ownFaq = pairs(termsSec.data?.faq)
  const ownNeed = pairs(termsSec.data?.working?.weNeed)
  const ownGet = pairs(termsSec.data?.working?.youGet)

  // Who works on it: the owner always; anyone the proposal names in content.team.
  // The owner's own entry (generated) carries their line and tasks for the
  // owner's profile rather than drawing a second page for them.
  const people = (Array.isArray(doc.team) ? doc.team : [])
    .map(p => ({ name: text(p.name), role: text(p.role || p.title), line: text(p.line), bio: text(p.bio), avatar: text(p.avatar_url || p.avatar), tasks: (p.tasks || []).map(text).filter(Boolean), owner: Boolean(p.owner) }))
    .filter(p => p.name)
  const ownerEntry = people.find(p => p.owner || p.name === owner.name) || null
  const team = people.filter(p => p !== ownerEntry)

  const ov = overview.data || {}
  const listOf = (v) => (Array.isArray(v) ? v : []).map((c, i) => ({ title: text(c?.title), body: text(c?.body), _i: i })).filter(c => c.title)

  return {
    look: doc.look,
    title: text(doc.title),
    // A headline written for the cover (older proposals carry one); the title
    // then becomes the line under it.
    headline: text(coverSec.data?.headline),
    recipient,
    contact,
    contactFirst: firstName(contact),
    reference: text(doc.reference),
    date: doc.date || null,
    closes: doc.closes || null,
    owner,
    overview: { index: overview.index, body: overview.data ? String(overview.data.body ?? '') : '' },
    challenges: listOf(ov.challenges),
    success: listOf(ov.success),
    glance: (Array.isArray(ov.glance) ? ov.glance : []).map(text).filter(Boolean),
    stages,
    milestones,
    scopeIndex: scope.index,
    blocks,
    totalWeeks,
    investment: { index: inv.index, rows, totals, total, monthly, schedule, note: text(invData.note), hasRows: rows.length > 0 },
    addons,
    options,
    proof: { index: proof.index, stats, quotes },
    cases,
    otherPages,
    bio: plain(text(aboutPage?.body) || text(about.data?.body) || text(settings.about)),
    team,
    ownerEntry,
    faq: ownFaq.length ? ownFaq : STANDARD_COPY.faq,
    weNeed: ownNeed.length ? ownNeed : STANDARD_COPY.weNeed,
    youGet: ownGet.length ? ownGet : STANDARD_COPY.youGet,
    vars,
    terms,
    termsAreOwn: ownTerms.length > 0,
    acceptance: { index: acceptance.index, body: text(acceptance.data?.body) },
    notIncluded: [...new Set(blocks.map(b => b.notIncluded).filter(Boolean))].join(' '),
  }
}

/**
 * The {{variables}} for this document: the client and contact, the package,
 * the quoted total, the weeks the services run, the validity date and the
 * owner. The same values the public page fills (proposalPageModel).
 */
export function modelVars(doc = {}, { settings = {}, byId = {} } = {}) {
  const sections = doc.sections || []
  const inv = sections.find(s => s.type === 'investment')?.data || {}
  const total = inv.totals
    ? (Number(inv.totals.oneTimeTotal) || 0) + (Number(inv.totals.commitment) || 0)
    : realRows(inv.rows).reduce((n, r) => n + (Number(r.amount) || 0), 0)
  const items = sections.find(s => s.type === 'scope')?.data?.items || []
  const ownerEntry = (Array.isArray(doc.team) ? doc.team : []).find(p => p?.owner)
  return proposalVars({
    company: text(doc.recipient),
    contact: text(doc.contact),
    packageName: text(doc.contentExtra?.package || doc.package),
    total,
    weeks: scheduleServices(items, byId).totalWeeks,
    validUntil: doc.closes || null,
    createdAt: doc.date || null,
    validityDays: Number(doc.validity_days) || 30,
    ownerName: text(settings.owner_name) || text(ownerEntry?.name) || text(settings.firm?.signer_name),
  })
}
